import { PartialAddress } from '../addressService/addressType'
import { Cart } from '../cartService/cart.service.types'

enum Currency {
  USD = 'USD',
  DEFAULT = 'DEFAULT',
}

export interface EnrichedCheckout {
  showLRE?: boolean
  id: string
  cartId: string
  paymentId?: number
  shippingAddressId?: string
  billingAddressId?: string
  status: 'COMPLETED' | 'OPEN' | 'CANCELLED'
  attributes: CheckoutAttribute[]
  shippingPackages: ShippingPackage[]
  phoneNumber?: string
  email?: string
  customerId?: number
  currencyCode: Currency
  authenticationKey?: string
  orderId?: number
  isStoreCreditUsed: boolean
  giftCardIds: string[]
  cart?: Cart
  costSummary?: CheckoutCostSummary
  paymentSummary?: CheckoutPaymentSummary
  giftCardBalanceList?: GiftCardBalance[]
  cartLineItemIdToFulfillmentInfoMap?: Record<string, CartLineItemShippingInfo>
  acceptsEmailMarketing: boolean
  acceptsTextMarketing: boolean
  availableFulfillmentOptions: AvailableFulfillmentOption[]
  selectedShippingMethod: ShippingMethod
  errors: CheckoutError[]
  customerDetails?: CustomerDetails
  shippingAddress: ShippingAddress
  billingAddress: ShippingAddress
  saveDetailsForFasterCheckout: boolean
  fulfillmentPackageTtl: string
  fulfillmentPackages?: FulfillmentPackage[]
  deliveryInstructions?: string
  billingAddressSameAsShippingAddress?: boolean
  unfulfillableCartLineItemDetails?: UnfulfillableCartLineItemDetails
  adjustmentCreditInfo?: {
    type: 'EXCHANGE'
    amount: number
  }
  isExchangeOrderCheckout: boolean
  taxBreakdown?: TaxBreakdown[]
  isShippingAddressPhoneNumberRequired?: boolean
  productProperties?: {
    product_id: string
    department_id: string
    department: string
    [key: string]: any
  }[]
}

export interface CheckoutExpressUpdateDetail {
  availableFulfillmentOptions: AvailableFulfillmentOption[] | undefined
  amountToCollect: number
  amountBreakdown: { label: string; amount: number }[]
  sessionId?: number
  checkoutId: string | undefined
  checkout: EnrichedCheckout | null | undefined
  fixedShippingAddress: ShippingAddress | undefined
  fixedEmailAddress: string | undefined
  isMto?: boolean
}

interface UnfulfillableCartLineItemDetails {
  cartLineItemIds: string[]
  comment: string
}

interface ShippingAddress {
  addressLine1: string
  addressLine2: string | null
  city: string
  countryCode: string
  customerId: number
  firstName: string
  id: string
  isActive: boolean
  isDefault: boolean
  lastName: string
  metaData: { isMilitary: boolean; isPoBox: boolean; isResidential: boolean }
  oldId: string | null
  phoneNumber: string
  provinceCode: string
  zip: string
}

interface CustomerDetails {
  accountCreatedAt: string
  customerId: number
  email: string
  firstName: string
  lastName: string
  phone: string
}

const SHIPPING_METHODS = {
  STANDARD: 'LB_STANDARD',
  SUPER: 'LB_SUPER',
  STANDARD_PREMIUM: 'LB_STANDARD_PREMIUM',
} as const

export type ShippingMethods = (typeof SHIPPING_METHODS)[keyof typeof SHIPPING_METHODS]

export const DEFAULT_SHIPPING_METHOD: ShippingMethods = 'LB_STANDARD'

interface BaseShippingMethod {
  name: ShippingMethods
  displayName: string
  amount?: number
}

type ShippingMethod = BaseShippingMethod &
  (
    | {
        name: 'LB_STANDARD'
        displayName: 'STANDARD'
      }
    | {
        name: 'LB_SUPER'
        displayName: 'EXPRESS'
      }
    | {
        name: 'LB_STANDARD_PREMIUM'
        displayName: 'STANDARD_PREMIUM'
      }
  )

const EDD_TYPE = {
  BUSINESS: 'BUSINESS',
  CALENDAR: 'CALENDAR',
}

type EddType = (typeof EDD_TYPE)[keyof typeof EDD_TYPE]

export interface AvailableFulfillmentOption {
  shippingMethod: ShippingMethod
  minDeliveryDate: string
  maxDeliveryDate: string
  shippingCost: number
  minDaysToDelivery: number
  maxDaysToDelivery: number
  eddType: EddType
  priorityExpressItemsQuantity?: number
  maxCalDays: number
  minCalDays: number
  fasterEdd?: boolean
}

interface GiftCardBalance {
  giftCardId: number
  currencyCode: Currency
  balanceAmount: number
  initialAmount: number
  maskedCode: string
}

interface CartLineItemShippingInfo {
  cartLineItemId: string
  maxDeliveryDate: string
  minDeliveryDate: string
  shippingMethod: ShippingMethod
  minCalDays: number
  maxCalDays: number
}

interface CheckoutPaymentSummary {
  paymentTransactionDetails?: TransactionDetail[]
  balanceAmount: number
  storeCreditAmountUsed?: number
}

enum TRANSACTION_MODE {
  STORE_CREDIT = 'STORE_CREDIT',
  GIFT_CARD = 'GIFT_CARD',
  PG = 'PG',
}

interface CardDetails {
  last4Digits: string
  cardType: string
}

type PGSource = 'PAYPAL' | 'CARD' | 'AMAZON_PAY_EXPRESS'

interface PaymentSourceMeta {
  cardDetails: CardDetails
  source: PGSource
}

interface UPGTransactionDetails {
  amount: number
  paymentSourceMeta: PaymentSourceMeta
  paymentGatewayProvider?: string
}

type TransactionType = 'REFUND' | 'PAYMENT_CAPTURE' | 'PAYMENT_CAPTURE_ROLLBACK' | 'REFUND_ROLLBACK'

type TransactionReasonCode =
  | 'ORDER_PAYMENT'
  | 'ORDER_CANCEL'
  | 'ORDER_RETURN'
  | 'ADJUST_CAPTURE_AMOUNT'
  | 'ADJUST_REFUND_AMOUNT'

interface TransactionDetailPG {
  mode: TRANSACTION_MODE.PG
  amount: number
  metaData?: UpgMetadata
  upgTransactionDetails: UPGTransactionDetails
  id: number
  status: string
  transactionReasonCode: TransactionReasonCode
  type: TransactionType
}

interface TransactionDetailGiftCard {
  mode: TRANSACTION_MODE.GIFT_CARD
  amount: number
  metaData?: GiftCardTransactionMetaData
  id: number
  status: string
  transactionReasonCode: TransactionReasonCode
  type: TransactionType
}

interface TransactionDetailStoreCredit {
  mode: TRANSACTION_MODE.STORE_CREDIT
  amount: number
  id: number
  status: string
  transactionReasonCode: TransactionReasonCode
  type: TransactionType
}

type TransactionDetail = TransactionDetailStoreCredit | TransactionDetailGiftCard | TransactionDetailPG

interface GiftCardTransactionMetaData {
  giftCardCodeSuffix: string
  giftCardId: number
}

interface UpgMetadata {
  sessionId: number
}

interface TaxBreakdown {
  taxName: string
  displayName: string
  amount: number
}

interface CheckoutCostSummary {
  subTotalAmount?: number
  subTotalAmountToDisplay?: number
  totalDiscount?: number
  totalDiscountToDisplay?: number
  totalShippingAmount?: number
  totalStoreCreditGrant?: number
  totalTax?: number
  totalAmount?: number
  taxBreakdown?: TaxBreakdown[]
}

interface ShippingOption {
  shippingMethod: ShippingMethod
  minDeliveryDate: string
  maxDeliveryDate: string
  cost: number
}

interface CheckoutAttribute {
  key: string
  value: string
}

interface ShippingPackage {
  id: string
  availableShippingOptions: ShippingOption[]
  selectedShippingOption: ShippingOption
  cartLineItemIds: string[]
}

interface CheckoutError {
  type: 'INVENTORY_OUT_OF_STOCK'
  metadata: CheckoutErrorMetaData
}

interface CheckoutErrorMetaData {
  affectedCartLineItemIds?: string[]
  isCompleteCartOutOfStock?: boolean
}

interface FulfillmentAllocationInfo {
  cartLineItemId: string
  quantity: number
  locationId: number
  vendorCode: string
  cost: number
  inventoryType: string
  tags: Record<string, unknown>
}

interface FulfillmentOption {
  shippingMethod: ShippingMethod
  minDeliveryDate: string
  maxDeliveryDate: string
  minDaysToDelivery: number
  maxDaysToDelivery: number
  shippingCost: number
  fulfillmentAllocationInfoList: FulfillmentAllocationInfo[]
  eddType: EddType
  cutoffEpoch: number | null
  cutoffTtlSec: number | null
  priorityExpressItemsQuantity: number
  fasterEdd?: boolean
}

interface FulfillmentPackage {
  id: string
  cartLineItemIds: string[]
  availableFulfillmentOptions: FulfillmentOption[]
  selectedFulfillmentOption: FulfillmentOption
}

export interface UpdateExpressCheckoutPartialAddressRequestDTO {
  partialAddress: PartialAddress
  upgSessionId: number
}
